import moment from "moment-timezone";
import { RangePickerPresets } from "../../components/antd/RangePicker";

export type TimeStep = "hour" | "day" | "week" | "month";
export type PageView = "metric" | "cost";
export type GroupBy =
  | "account"
  | "org"
  | "gvc"
  | "location"
  | "metric"
  | "feature"
  // TODO update type to support any arbitrary value from features
  // rest is from chargeable items
  | "workloads"
  | "volume-sets"
  | "custom-ingress"
  | "observability";

export interface UsageRequestBody {
  detailed: boolean;
  startTime: string;
  endTime: string;
  aggregateByTimeStep: boolean;
  timeStep: TimeStep;
  groupBy: string[];
  consumptionQueries: { id: string; filterBy: Record<string, string>; aggregateBy: string[] }[];
}

export function GroupByToLabel(groupBy: GroupBy): string {
  switch (groupBy) {
    case "account":
      return "Account";
    case "org":
      return "Org";
    case "gvc":
      return "GVC";
    case "location":
      return "Location";
    case "metric":
      return "Metric";
    case "feature":
      return "Feature";
    case "workloads":
      return "Workload";
    case "volume-sets":
      return "Volume Set";
    case "custom-ingress":
      return "Custom Ingress";
    case "observability":
      return "Observability";
    default:
      return groupBy;
  }
}

export type NumberMap = { [_: string]: number };

export interface ChargeableItem {
  id: string;
  name: string;
  description: string;
  consumptionTags: { [_: string]: string };
  unit: string; // "cores * seconds"
  type: "consumption" | "surcharge"; // "cores * seconds"
}

export interface PeriodConsumption {
  total: number;
  projectedTotal?: number;
  value: number;
  projectedValue?: number;
  currency: "USD" | string;
  ratePlans: string[];
  tags: { [_: string]: string };
}

export interface PeriodGroup {
  key: { [_: string]: string };
  consumptions: PeriodConsumption[];
}

export interface Period {
  startTime: string;
  endTime: string;
  totalSeconds: number;
  elapsedSeconds?: number;
  consumptionCount: number;
  groups: PeriodGroup[];
}

export type OrderedArray = [number, string][];

export interface GraphData {
  periodTimes: string;

  primaryTotalMap: NumberMap;
  primaryTotalMapHidden: NumberMap;
  primaryTotalOthers: NumberMap;
  primaryTotalMapForDisplay: NumberMap;

  primaryTotalMapOrderedRaw: OrderedArray;

  secondaryTotalMap: NumberMap;
  secondaryTotalMapHidden: NumberMap;
  secondaryTotalOthers: NumberMap;
  secondaryTotalMapForDisplay: NumberMap;

  secondaryTotalMapOrderedRaw: OrderedArray;

  // secondary cost is ignored for the projected
  projectedTotalMap: NumberMap;
  projectedTotalMapHidden: NumberMap;
  projectedTotalOthers: NumberMap;
  projectedTotalMapForDisplay: NumberMap;

  valueMap: NumberMap;
  valueMapHidden: NumberMap;
  valueOthers: NumberMap;
  valueMapForDisplay: NumberMap;

  projectedValueMap: NumberMap;
  projectedValueMapHidden: NumberMap;
  projectedValueOthers: NumberMap;
  projectedValueMapForDisplay: NumberMap;

  valueMapOrderedRaw: OrderedArray;
}

export interface ChartUnit {
  dataKey: string;
  label: string;
  org?: string;
  gvc?: string;
  primaryTotalSum: number;
  secondaryTotalSum: number;
  projectedTotalSum: number;
  color: string;
}
export interface ChartData {
  units: ChartUnit[];
  graphData: GraphData[];
  metricUnit: string;
}

interface FormatXLabelProps {
  value: string;
  timeStep: TimeStep;
}

export function formatXLabel({ value, timeStep }: FormatXLabelProps): string {
  try {
    let startTime = moment(new Date(value.split(" ")[0]));
    let endTime = moment(new Date(value.split(" ")[1]));
    if (timeStep === "hour") {
      return startTime.format("HH:mm");
    }
    startTime = moment.utc(new Date(value.split(" ")[0]));
    endTime = moment.utc(new Date(value.split(" ")[1]));
    if (timeStep === "day") {
      return startTime.format("MMM-DD");
    }
    if (timeStep === "week") {
      return `${startTime.format("MMM-DD")} - ${endTime.format("MMM-DD")}`;
    }
    if (timeStep === "month") {
      return startTime.format("MMM-YY").replace("-", " '");
    }
    return value;
  } catch (e) {
    return value;
  }
}

export function formatXLabelSpreadsheet(periodTimes: string, timeStep: TimeStep): string {
  try {
    let startTimeIso = periodTimes.split(" ")[0];
    let endTimeIso = periodTimes.split(" ")[1];
    if (timeStep !== "hour") {
      startTimeIso = startTimeIso.split("T")[0];
      endTimeIso = endTimeIso.split("T")[0];
    }
    return `${startTimeIso} - ${endTimeIso}`;
  } catch (e) {
    return periodTimes;
  }
}

export function formatPrice(value: number) {
  return Number(value.toFixed(2));
}

export const featureLabelMap: { [_: string]: string } = {
  workloads: "Workloads",
  "custom-ingress": "Custom Ingress",
  "volume-sets": "Volume Sets",
  observability: "Observability",
};

export const chargeableItemLabelMap = {
  "ecbb3f5f-0b22-4c83-ba8e-1a36f899aeac": "Workloads - CPU",
  "6b611577-4bd8-44fa-9097-909a95242de1": "Workloads - Memory",
  "9f85f07d-e7cf-4931-a3c9-15cef006127a": "Workloads - Egress",
  "c9cf95aa-003f-4157-a214-5e5203f1fe0f": "Custom Ingress - Load Balancer",
  "3abd25b0-a886-43d8-808d-4e7499d4414f": "Volume Sets - Capacity - High Throughput SSD",
  "d7429d7a-e0dd-4334-a224-ade15c476f64": "Volume Sets - Capacity - General Purpose SSD",
  "8a049309-c074-4343-8e93-7e8dfde9d3cc": "Workloads - Replicas",
};

export type chargeableItemUnitProcessor = (value: number, elapsedSeconds: number, totalSeconds: number) => number;

export type TChargeableItemUnitsAsMap = {
  cores: any; //
  bytes: any;
  seconds: any;
  "Load Balancers": any;
  GB: any;
  Replicas: any;
  Seconds: any;
}; //

export type TChargeableItemUnit =  //
  | "cores" //
  | "bytes"
  | "seconds"
  | "Load Balancers"
  | "GB"
  | "Replicas"
  | "Seconds";

export const chargeableItemUnits: TChargeableItemUnit[] = [
  //
  "cores", //
  "bytes",
  "seconds",
  "Load Balancers",
  "GB",
  "Replicas",
  "Seconds",
];

// export function ChargeableItemMetricToLabel(metric: string): string {
//   switch (metric) {
//     case "cores * seconds":
//       return "Core";
//     case "bytes * seconds":
//       return "GiB";
//     case "bytes":
//       return "GB";
//     case "Load Balancers * Seconds":
//       return "Load Balancer";
//     case "GB * Seconds":
//       return "GB";
//     case "Replicas * Seconds":
//       return "Replica";
//     default:
//       return metric;
//   }
// }

export type TChargeableItemUnitProcessorMap = {
  [Property in keyof TChargeableItemUnitsAsMap]: chargeableItemUnitProcessor[];
};

export const chargeableItemUnitProcessorMap: TChargeableItemUnitProcessorMap = {
  cores: [],
  bytes: [
    (value, e, t) => {
      const gibDivider = 1_073_741_824;
      const tbDivider = 1_099_511_627_776;
      const mibDivider = gibDivider / 1_024;
      // const divider = isMiB ? mibDivider : gibDivider;
      // const result = value / gibDivider;
      const result = value / tbDivider;
      return result;
    },
  ],
  seconds: [
    (value, elapsedSeconds, totalSeconds) => {
      const result = value / (elapsedSeconds || totalSeconds);
      return result;
    },
  ],
  "Load Balancers": [],
  GB: [
    (value, elapsedSeconds, totalSeconds) => {
      let result = 0;
      // if (!isMiB) {
      result = value;
      // } else {
      //   result = value * 1_024;
      // }
      return result;
    },
  ],
  Replicas: [],
  Seconds: [
    (value, elapsedSeconds, totalSeconds) => {
      const result = value / (elapsedSeconds || totalSeconds);
      return result;
    },
  ],
};

export function getUsageRequestBody(
  startTime: string,
  endTime: string,
  timeStep: TimeStep,
  pageView: PageView,
  groupBy: GroupBy,
  chargeableItems: ChargeableItem[],
  features: string[],
): UsageRequestBody {
  if (groupBy === "account") {
    groupBy = "org";
  }
  let groupByList: string[] = [];
  if (pageView === "metric") {
    groupByList.push("metric");
  }
  if (features.includes(groupBy)) {
    groupByList.push("name");
  } else {
    groupByList.push(groupBy);
  }

  // workloads and volume-sets features require also grouping by gvc
  if (["workloads", "volume-sets"].includes(groupBy)) {
    groupByList.push("gvc");
  }

  return {
    detailed: false,
    startTime: startTime,
    endTime: endTime,
    aggregateByTimeStep: true,
    timeStep: timeStep,
    groupBy: groupByList,
    consumptionQueries: chargeableItems.map((_c) => {
      const c: ChargeableItem = JSON.parse(JSON.stringify(_c));
      const aggregateBy: string[] = [];
      if (features.includes(groupBy)) {
        aggregateBy.push("name");
      } else {
        aggregateBy.push(groupBy);
      }
      if (groupBy !== "metric") {
        aggregateBy.push("metric");
      }
      if (["workloads", "volume-sets"].includes(groupBy)) {
        aggregateBy.push("gvc");
      }
      for (const consumptionTagKey of Object.keys(c.consumptionTags)) {
        if (consumptionTagKey === "metric") {
          continue;
        }
        aggregateBy.push(consumptionTagKey);
      }

      return {
        id: c.id,
        filterBy: c.consumptionTags,
        aggregateBy: aggregateBy,
      };
    }),
  };
}

export function getUsageHourPresets(): RangePickerPresets {
  const n = () => moment();

  return [
    {
      label: "Last 3 Hours",
      value: [n().add(1, "hour").startOf("hour").subtract(3, "hour"), n().add(1, "hour").startOf("hour")],
    },
    {
      label: "Last 6 Hours",
      value: [n().add(1, "hour").startOf("hour").subtract(6, "hour"), n().add(1, "hour").startOf("hour")],
    },
    {
      label: "Last 12 Hours",
      value: [n().add(1, "hour").startOf("hour").subtract(12, "hour"), n().add(1, "hour").startOf("hour")],
    },
    {
      label: "Last 24 Hours",
      value: [n().add(1, "hour").startOf("hour").subtract(24, "hour"), n().add(1, "hour").startOf("hour")],
    },
    {
      label: "Today",
      value: [n().startOf("day"), n().add(1, "day").startOf("day")],
    },
    {
      label: "Yesterday",
      value: [n().startOf("day").subtract(1, "day"), n().startOf("day")],
    },
  ];
}

export function getUsageDayPreset(): RangePickerPresets {
  const n = () => moment();

  return [
    { label: "Last 7 Days", value: [n().startOf("day").subtract(6, "day"), n().add(1, "day").startOf("day")] },
    { label: "Last 15 Days", value: [n().startOf("day").subtract(14, "day"), n().add(1, "day").startOf("day")] },
    { label: "Last 30 Days", value: [n().startOf("day").subtract(29, "day"), n().add(1, "day").startOf("day")] },
    { label: "This Month", value: [n().startOf("month"), n().add(1, "month").startOf("month")] },
    { label: "Last Month", value: [n().subtract(1, "month").startOf("month"), n().startOf("month")] },
  ];
}

export function getUsageWeekPreset(): RangePickerPresets {
  const n = () => moment();

  return [
    {
      label: "Last 6 Weeks",
      value: [n().startOf("week").subtract(5, "week").add(1, "day"), n().startOf("week").add(1, "week").add(1, "day")],
    },
    { label: "This Month", value: [n().startOf("month"), n().add(1, "month").startOf("month")] },
    { label: "Last Month", value: [n().subtract(1, "month").startOf("month"), n().startOf("month")] },
  ];
}

export function getUsageMonthPreset(): RangePickerPresets {
  const n = () => moment();

  return [
    {
      label: "Last 3 Months",
      value: [n().subtract(2, "month").startOf("month"), n().add(1, "month").startOf("month")],
    },
    {
      label: "Last 6 Months",
      value: [n().subtract(5, "month").startOf("month"), n().add(1, "month").startOf("month")],
    },
    {
      label: "Last 12 Months",
      value: [n().subtract(11, "month").startOf("month"), n().add(1, "month").startOf("month")],
    },
    {
      label: "Last 24 Months",
      value: [n().subtract(23, "month").startOf("month"), n().add(1, "month").startOf("month")],
    },
    { label: "This Year", value: [n().startOf("year"), n().add(1, "year").startOf("year")] },
    { label: "Last Year", value: [n().subtract(1, "year").startOf("year"), n().startOf("year")] },
  ];
}

export type IEC_UNIT = "B" | "KiB" | "MiB" | "GiB" | "TiB";
export type DECIMAL_UNIT = "B" | "KB" | "MB" | "GB" | "TB";

const IEC_MULTIPLIERS: Record<IEC_UNIT, number> = {
  B: 1,
  KiB: 1024,
  MiB: 1024 ** 2,
  GiB: 1024 ** 3,
  TiB: 1024 ** 4,
};

export const IEC_UNITS = Object.keys(IEC_MULTIPLIERS) as IEC_UNIT[];

const DECIMAL_MULTIPLIERS: Record<DECIMAL_UNIT, number> = {
  B: 1,
  KB: 1000,
  MB: 1000 ** 2,
  GB: 1000 ** 3,
  TB: 1000 ** 4,
};

export const DECIMAL_UNITS = Object.keys(DECIMAL_MULTIPLIERS) as DECIMAL_UNIT[];

export const IEC_Helpers = {
  convertInputToBytes(value: number, unit: IEC_UNIT) {
    const multiplier = IEC_MULTIPLIERS[unit];
    return value * multiplier;
  },
  convertInputArrayToBytes(values: number[], unit: IEC_UNIT) {
    return values.map((value) => this.convertInputToBytes(value, unit));
  },
  getBestDisplayUnit(bytesList: number[]): IEC_UNIT {
    const max = Math.max(...bytesList);
    let exponent = Math.floor(Math.log(max) / Math.log(1024));
    exponent = Math.min(exponent, 4); // Clamp to TiB max
    return ["B", "KiB", "MiB", "GiB", "TiB"][exponent] as IEC_UNIT;
  },
  convertBytesToUnit(bytes: number, targetUnit: IEC_UNIT) {
    const multiplier = IEC_MULTIPLIERS[targetUnit];
    return bytes / multiplier;
  },
  convertBytesArrayToUnit(bytesList: number[], targetUnit: IEC_UNIT) {
    return bytesList.map((input) => this.convertBytesToUnit(input, targetUnit));
  },
};

export const DECIMAL_Helpers = {
  convertInputToBytes(value: number, unit: DECIMAL_UNIT) {
    const multiplier = DECIMAL_MULTIPLIERS[unit];
    return value * multiplier;
  },
  convertInputArrayToBytes(values: number[], unit: DECIMAL_UNIT) {
    return values.map((value) => this.convertInputToBytes(value, unit));
  },
  getBestDisplayUnit(bytesList: number[]): DECIMAL_UNIT {
    const max = Math.max(...bytesList);
    let exponent = Math.floor(Math.log(max) / Math.log(1000));
    exponent = Math.min(exponent, 4); // Clamp to TB max
    return ["B", "KB", "MB", "GB", "TB"][exponent] as DECIMAL_UNIT;
  },
  convertBytesToUnit(bytes: number, targetUnit: DECIMAL_UNIT) {
    const multiplier = DECIMAL_MULTIPLIERS[targetUnit];
    return bytes / multiplier;
  },
  convertBytesArrayToUnit(bytesList: number[], targetUnit: DECIMAL_UNIT) {
    return bytesList.map((input) => this.convertBytesToUnit(input, targetUnit));
  },
};

export interface OnMouseMoveParam {
  activeCoordinate: { x: number; y: number };
  activeLabel: string;
  activePayload: TooltipActivePayload[];
  activeTooltipIndex: number;
  chartX: number;
  chartY: number;
  isTooltipActive: boolean;
}

interface TooltipActivePayload {
  color: string;
  dataKey: string;
  fill: string;
  formatter: any;
  name: string;
  payload: GraphData;
  type: any;
  unit: string;
  value: number;
}
